import { NoteDataAccessContext } from "src/components/note-data-access-context";
import { dataAccessScope } from "src/configs/subscription-access";
import { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { NoteChartErrorContext } from "src/components/note-chart-error-context";
import { NoteChartRenderContext } from "src/components/note-chart-render-context";
import { Responses } from "src/utils/data";
import { shallowEqual, useSelector } from "react-redux";
import { thunks as datalakeApi } from "src/thunks/datalake";

const EMPTY_DATA = {};

export const useDataLake = (requests, options = {}) => {
  const noteScope = useContext(NoteDataAccessContext);
  const accessScope = noteScope ? `${dataAccessScope().split(":")[0]}:${noteScope}` : dataAccessScope();
  const noteRendering = useContext(NoteChartRenderContext);
  const isolateUpdates = options.isolateUpdates ?? noteRendering;
  const dispatch = useDispatch();
  const reportError = useContext(NoteChartErrorContext);
  const [settledKeys, setSettledKeys] = useState(null);
  const state = useSelector((store) => {
    const scoped = store.datalake.__scopes?.[accessScope] || EMPTY_DATA;
    if (!isolateUpdates) return scoped;
    const selected = {};
    for (const request of requests) {
      if (!request) continue;
      const key = request.getKey();
      if (key in scoped) selected[key] = scoped[key];
    }
    return selected;
  }, isolateUpdates ? shallowEqual : undefined);

  let keys = "";
  for (var r of requests) {
    if (r !== undefined && r !== null) {
      keys += r.getKey();
    }
  }

  useEffect(() => {
    let cancelled = false;
    setSettledKeys(null);
    Promise.resolve(dispatch(datalakeApi.getData(requests, accessScope))).then((error) => {
      if (cancelled) return;
      setSettledKeys(keys);
      if (!error) return;
      const forbidden = [401, 403].includes(error?.response?.status);
      reportError?.(forbidden ? "차트 데이터를 열람할 권한이 없습니다."
        : "차트 데이터를 불러오지 못했습니다.", !forbidden);
    });
    return () => { cancelled = true; };
    // Request keys define the identity; changing request instances must not retry failures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, keys, reportError, accessScope]);

  const buildResult = () => {
    let initialized = true;
    const result = [];
    for (const request of requests) {
      if (request && request.getKey() in state) {
        result.push(state[request.getKey()]);
      } else {
        initialized = false;
      }
    }
    return { initialized, loading: !initialized && settledKeys !== keys, responses: new Responses(result) };
  };
  const stableResult = useMemo(() => isolateUpdates ? buildResult() : null,
    // Request keys define the data identity; object instances may change every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, keys, isolateUpdates, settledKeys]);
  // Preserve the original response identity behavior outside opt-in note viewers.
  return isolateUpdates ? stableResult : buildResult();
};
